import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, Clock, AlertTriangle, CheckCircle, RefreshCw, ExternalLink, X } from 'lucide-react';
import { calendarService } from '../../services/api';
import useModal from '../../hooks/useModal';
import useNotification from '../../hooks/useNotification';
import Notification from '../common/Notification';

const OutlookCalendarIntegration = ({ user, onStatusChange }) => {
  const { notification, showSuccess, hideNotification } = useNotification();
  const [connectionStatus, setConnectionStatus] = useState({
    connected: false,
    loading: true,
    error: null,
    token_valid: false,
    last_sync: null,
    sync_errors: 0
  });
  const [syncing, setSyncing] = useState(false);
  const [syncLogs, setSyncLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);

  // Modal behavior
  const { handleBackdropMouseDown, handleBackdropMouseUp } = useModal(showLogs, () => setShowLogs(false));

  const checkConnectionStatus = useCallback(async () => {
    try {
      setConnectionStatus(prev => ({ ...prev, loading: true }));
      const response = await calendarService.outlookCalendar.getConnectionStatus();

      setConnectionStatus({
        connected: response.data.connected,
        loading: false,
        error: null,
        token_valid: response.data.token_valid,
        last_sync: response.data.last_sync,
        sync_errors: response.data.sync_errors,
        token_expires_at: response.data.token_expires_at
      });

      if (onStatusChange) {
        onStatusChange(response.data);
      }
    } catch (error) {
      setConnectionStatus({
        connected: false,
        loading: false,
        error: error.response?.data?.detail || 'Failed to check connection status',
        token_valid: false,
        last_sync: null,
        sync_errors: 0
      });
    }
  }, [onStatusChange]);

  useEffect(() => {
    checkConnectionStatus();
  }, [checkConnectionStatus]);

  const handleConnect = async () => {
    // Open popup synchronously during click to avoid Safari popup blocker
    const popup = window.open('about:blank', 'outlook-calendar-oauth', 'width=500,height=600,scrollbars=yes,resizable=yes');

    if (!popup) {
      setConnectionStatus(prev => ({
        ...prev,
        error: 'Popup blocked by your browser. Please allow popups for this site and try again.'
      }));
      return;
    }

    try {
      const response = await calendarService.outlookCalendar.initiateConnection();
      popup.location.href = response.data.authorization_url;

      // Listen for postMessage from OAuth callback
      const handleMessage = (event) => {
        if (event.data?.type === 'outlook-calendar-connected') {
          window.removeEventListener('message', handleMessage);
          clearInterval(checkClosed);
          showSuccess('Outlook Calendar connected successfully!');
          checkConnectionStatus();
        } else if (event.data?.type === 'outlook-calendar-error') {
          window.removeEventListener('message', handleMessage);
          clearInterval(checkClosed);
          setConnectionStatus(prev => ({
            ...prev,
            error: event.data.error || 'Connection failed'
          }));
        }
      };
      window.addEventListener('message', handleMessage);

      // Fallback: listen for popup to close
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          window.removeEventListener('message', handleMessage);
          setTimeout(() => {
            checkConnectionStatus();
          }, 1000);
        }
      }, 1000);

    } catch (error) {
      popup.close();
      setConnectionStatus(prev => ({
        ...prev,
        error: error.response?.data?.detail || 'Failed to initiate connection'
      }));
    }
  };

  const handleDisconnect = async () => {
    try {
      await calendarService.outlookCalendar.disconnect();
      setConnectionStatus({
        connected: false,
        loading: false,
        error: null,
        token_valid: false,
        last_sync: null,
        sync_errors: 0
      });

      if (onStatusChange) {
        onStatusChange({ connected: false });
      }
    } catch (error) {
      setConnectionStatus(prev => ({
        ...prev,
        error: error.response?.data?.detail || 'Failed to disconnect'
      }));
    }
  };

  const handleImportFromOutlook = async () => {
    try {
      setSyncing(true);
      const response = await calendarService.outlookCalendar.importFromOutlook();

      // Refresh status after import
      await checkConnectionStatus();

      // Show success message
      showSuccess(`Import completed! ${response.data.imported_events} events imported, ${response.data.skipped_events} skipped.`);

    } catch (error) {
      setConnectionStatus(prev => ({
        ...prev,
        error: error.response?.data?.detail || 'Import failed'
      }));
    } finally {
      setSyncing(false);
    }
  };

  const handleFullSync = async () => {
    try {
      setSyncing(true);
      const response = await calendarService.outlookCalendar.fullSync();

      // Refresh status after sync
      await checkConnectionStatus();

      // Show success message
      showSuccess(`Sync completed! ${response.data.synced_events} events synced.`);

    } catch (error) {
      setConnectionStatus(prev => ({
        ...prev,
        error: error.response?.data?.detail || 'Sync failed'
      }));
    } finally {
      setSyncing(false);
    }
  };

  const loadSyncLogs = async () => {
    try {
      const response = await calendarService.outlookCalendar.getSyncLogs();
      setSyncLogs(response.data.logs);
      setShowLogs(true);
    } catch (error) {
      console.error('Failed to load sync logs:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  if (connectionStatus.loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center space-x-2">
          <RefreshCw className="h-5 w-5 animate-spin text-gray-600" />
          <span>Checking Outlook Calendar connection...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <Calendar className="h-6 w-6" style={{ color: '#0078d4' }} />
            <h3 className="text-lg font-semibold">Outlook Calendar Integration</h3>
          </div>

          <div className="flex items-center space-x-2">
            {connectionStatus.connected ? (
              <div className="flex items-center space-x-2 text-green-600">
                <CheckCircle className="h-5 w-5" />
                <span className="text-sm font-medium">Connected</span>
              </div>
            ) : (
              <div className="flex items-center space-x-2 text-gray-500">
                <X className="h-5 w-5" />
                <span className="text-sm font-medium">Not Connected</span>
              </div>
            )}
          </div>
        </div>

        {connectionStatus.error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              <span className="text-sm text-red-700">{connectionStatus.error}</span>
            </div>
          </div>
        )}

        {connectionStatus.connected && !connectionStatus.token_valid && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="h-4 w-4 text-yellow-500" />
              <span className="text-sm text-yellow-700">
                Token expired. Please reconnect to Outlook Calendar.
              </span>
            </div>
          </div>
        )}

        <div className="space-y-4">
          {connectionStatus.connected ? (
            <>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Last Sync:</span>
                  <div className="font-medium">{formatDate(connectionStatus.last_sync)}</div>
                </div>
                <div>
                  <span className="text-gray-500">Sync Errors:</span>
                  <div className="font-medium">
                    {connectionStatus.sync_errors > 0 ? (
                      <span className="text-red-600">{connectionStatus.sync_errors}</span>
                    ) : (
                      <span className="text-green-600">0</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={handleFullSync}
                  disabled={syncing}
                  className="flex items-center space-x-2 px-4 py-2 text-white rounded-md hover:opacity-90 disabled:opacity-50"
                  style={{ backgroundColor: '#0078d4' }}
                >
                  <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
                  <span>{syncing ? 'Syncing...' : 'Full Sync'}</span>
                </button>

                <button
                  onClick={handleImportFromOutlook}
                  disabled={syncing}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                >
                  <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
                  <span>{syncing ? 'Importing...' : 'Import from Outlook'}</span>
                </button>

                <button
                  onClick={loadSyncLogs}
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                >
                  <Clock className="h-4 w-4" />
                  <span>View Logs</span>
                </button>

                <button
                  onClick={handleDisconnect}
                  className="flex items-center space-x-2 px-4 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200"
                >
                  <X className="h-4 w-4" />
                  <span>Disconnect</span>
                </button>
              </div>
            </>
          ) : (
            <div className="text-center py-4">
              <p className="text-gray-600 mb-4">
                Connect your Outlook Calendar to sync events automatically.
              </p>
              <button
                onClick={handleConnect}
                className="flex items-center space-x-2 px-6 py-2 text-white rounded-md hover:opacity-90 mx-auto"
                style={{ backgroundColor: '#0078d4' }}
              >
                <Calendar className="h-4 w-4" />
                <span>Connect Outlook Calendar</span>
                <ExternalLink className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Sync Logs Modal */}
      {showLogs && (
        <div
          className="fixed inset-0 flex items-center justify-center p-4 z-[9999]"
          onMouseDown={handleBackdropMouseDown}
          onMouseUp={handleBackdropMouseUp}
        >
          <div className="fixed inset-x-0 top-16 bottom-0 bg-black bg-opacity-60 -z-10"></div>
          <div
            className="bg-white rounded-lg max-w-2xl w-full max-h-96 overflow-hidden"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h4 className="text-lg font-semibold">Outlook Sync Logs</h4>
              <button
                onClick={() => setShowLogs(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto max-h-80">
              {syncLogs.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No sync logs available</p>
              ) : (
                <div className="space-y-2">
                  {syncLogs.map((log, index) => (
                    <div
                      key={index}
                      className={`p-3 rounded-md border ${
                        log.status === 'success'
                          ? 'bg-green-50 border-green-200'
                          : 'bg-red-50 border-red-200'
                      }`}
                    >
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium">
                          {log.provider} - {log.operation}
                        </span>
                        <span className="text-gray-500">
                          {formatDate(log.created_at)}
                        </span>
                      </div>
                      {log.error_message && (
                        <p className="text-red-600 text-sm mt-1">{log.error_message}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Notification
        isOpen={notification.isOpen}
        onClose={hideNotification}
        title={notification.title}
        message={notification.message}
        variant={notification.variant}
        duration={notification.duration}
      />
    </div>
  );
};

export default OutlookCalendarIntegration;
